import "./OfficeForm.css";
import Navbar from "../components/Navbar/Navbar";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "../api/apiCall";
import { Company } from "../models/company";
import OfficeCell from "./OfficeCell";
import { useNavigate } from "react-router-dom";
import generateToastContainer from "../utils/ToastContainer";
import TopHeader from "../components/Navbar/TopHeader";
import { User } from "../models/user";
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function OfficeForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const {
    data,
    isLoading: isLoadingOffices,
    isError,
    error,
  } = useQuery<Company[], Error>({
    queryKey: ["fetchAllCompanies"],
    queryFn: async () => apiCall<Company[]>("/companies", { method: "GET" }),
    enabled: isAuthReady,
  });

  const { data: currentUser, isLoading: isLoadingMe } = useQuery<User, Error>({
    queryKey: ["fetchCurrentUser"],
    queryFn: async () => apiCall<User>("/users/me", { method: "GET" }),
    enabled: isAuthReady,
  });

  const userToGlimpse = currentUser as any;
  const userSubscription =
    userToGlimpse?.Company?.Subscription ||
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;

  const currentPlan =
    userSubscription?.status === "active" ? userSubscription.plan_code : "free";

  const isLoading = isLoadingOffices || isLoadingMe;

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />
        <FeatureGate
          userPlan={currentPlan}
          requiredPlan="essential"
          featureName="la gestion des entreprises"
        >
          <p className="loading-text">{t.offices.loading}</p>
        </FeatureGate>
      </div>
    );
  }

  if (isError) {
    console.error("[OfficeForm] Error loading offices:", error);
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />
        <FeatureGate
          userPlan={currentPlan}
          requiredPlan="essential"
          featureName="la gestion des entreprises"
        >
          <p className="error-text">
            {t.offices.errorLoading} {error.message}
          </p>
        </FeatureGate>
      </div>
    );
  }

  const companies: Company[] = data ?? [];

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />

      <main className="dashboard-content">
        {/* Utilisation de var(--text-main) pour s'adapter au Dark Mode */}
        <h1
          style={{
            color: "var(--text-main, #1e293b)",
            margin: 0,
            fontSize: "1.8rem",
            fontWeight: "800",
          }}
        >
          {t.offices.title}
        </h1>
        <p className="dashboard-subtitle">{t.offices.subtitle}</p>

        <FeatureGate
          userPlan={currentPlan}
          requiredPlan="essential"
          featureName="la gestion des entreprises"
        >
          <div className="office-grid">
            {companies.length === 0 ? (
              <p className="empty-offices-text">{t.offices.noOffices}</p>
            ) : (
              companies.map((company) => (
                <OfficeCell key={company.uuid} company={company} />
              ))
            )}

            <div className="add-office-actions">
              <button
                type="button"
                className="add-policy-submit"
                onClick={() => navigate("/add-office")}
              >
                {t.offices.addBtn}
              </button>
              {generateToastContainer()}
            </div>
          </div>
        </FeatureGate>
      </main>
    </div>
  );
}

export default OfficeForm;
